// ============================================================================
// client/vignette-papier.ts — LE MODE CRÉATION EN PAPIER DÉCOUPÉ
// ----------------------------------------------------------------------------
// Demande de Claude-Alain du 29/07 : « le mode création doit aussi avoir le
// même style graphique ». L'éditeur et les miniatures de niveaux étaient les
// deux derniers endroits à blitter `spritesheet.png` — on y construisait donc
// une carte dans un langage, et on la jouait dans un autre.
//
// Ce module est le PONT. L'éditeur parle en CARACTÈRES ('#', 'H', '$'), les six
// modules papier parlent en formes. Une seule table de correspondance ici, un
// seul point d'entrée : `blitVignette`.
//
// ⚠️ DEUX PIÈGES, tous les deux déjà payés ailleurs dans cette refonte.
//
//   1. L'ÉCHELLE. Toute la planche de tuiles est réglée pour une case de 48 px
//      (« y + S - 10 », « arc(..., 3.4) », des dizaines de constantes). Une
//      vignette de palette fait 30 px, une grande carte descend plus bas encore.
//      On ne proportionne donc RIEN : on peint à 48, une fois, et on réduit
//      l'image. Le dessin reste juste à toutes les tailles.
//   2. LE CACHE, qui n'est pas ici une optimisation mais la condition d'entrée.
//      `redraw()` repeint la grille ENTIÈRE (jusqu'à ~780 cases) à chaque
//      mouvement de souris, et une tuile de papier coûte des milliers de
//      fillRect. Sans cache, l'éditeur serait injouable.
// ============================================================================

import { BIG_DECOS, DECOS, type BigDecoKind, type DecoKind } from '../shared/decor'
import { T } from '../shared/types'
import type { WorldKey } from '../shared/worlds'
import { drawBaroudeurPapier } from './baroudeur-papier'
import { blitDeco } from './deco-papier'
import { drapeauPapier } from './drapeau-papier'
import { blitMoine } from './moine-papier'
import { fishPapier, objetPapier } from './objet-papier'
import { setDetail } from './papier'
import { BIOMES, tuilePapier } from './terrain-papier'

type Ctx = CanvasRenderingContext2D

/** La taille à laquelle tout est peint, toujours. Voir le piège n° 1. */
const S = 48
/** Marge HAUTE du canvas de peinture. Ce qui tue porte une frange DENTELÉE qui
 *  sort par le haut de sa case (`dents()` pointe vers le haut depuis y + 1) ;
 *  sans cette marge, la lave, le feu et les pics perdaient dans l'éditeur le
 *  signe même qui dit qu'ils tuent. Le blit compense le décalage, la case tombe
 *  donc exactement où l'appelant l'a demandée. */
const MARGE = 8
/** Marge LATÉRALE. Les moines portent leur attribut sur le côté (le sac du
 *  voleur, la truelle du bâtisseur) et `blitMoine` réserve exprès 55 % de
 *  débord de chaque côté — or cet attribut est le SEUL signe qui distingue les
 *  cinq moines. Sans marge horizontale, la vignette les rendait identiques.
 *  Même faute que les dents de la lave, un axe plus loin. */
const MARGE_X = 14

/* char -> tuile de terrain. Miroir de TILE_CHARS (shared/level.ts) ; on garde la
   table explicite plutôt que de la dériver, parce que l'éditeur a des chars que
   le terrain n'a pas (E, les téléporteurs par paire) et l'inverse. */
const TUILE_PAR_CHAR: Record<string, string> = {
  '#': 'Brick', '=': 'Concrete', 'H': 'Ladder', '-': 'Rope', 'x': 'FalseBrick',
  'I': 'Ice', 's': 'Sand', 'c': 'Crumble', 'L': 'Lava', '~': 'Water', ':': 'Bubbles',
  '°': 'Button', '¦': 'Barrier', '»': 'OneWayR', '«': 'OneWayL', '¤': 'DashCrystal',
  '/': 'RampUpRight', '\\': 'RampUpLeft', '+': 'Fire', '"': 'Spikes', 'Z': 'DeathZone',
  '<': 'ConveyorL', '>': 'ConveyorR', 'n': 'ConveyorUp', 'w': 'ConveyorDown',
  '^': 'Trampoline', 'A': 'TrampolineStrong', '{': 'TrampolineStrongL', '}': 'TrampolineStrongR',
  '(': 'TrampolineL', ')': 'TrampolineR', 'R': 'BombRefill', 'D': 'DoorClosed',
  'E': 'ExitLadder', '_': 'Checkpoint',
}

/* Les trois paires de téléporteurs gardent leur couleur : c'est elle qui dit
   où l'on ressort, l'éditeur doit la montrer comme le jeu. */
const TELEPORTEUR_PAR_CHAR: Record<string, number> = {
  'T': 0, 't': 0, 'U': 1, 'u': 1, 'V': 2, 'v': 2,
}

/* char -> objet ramassable ou caisse. */
const OBJET_PAR_CHAR: Record<string, string> = {
  '$': 'or', 'b': 'bomb', 'j': 'jackhammer', 'z': 'tazer', 'k': 'key', 'r': 'rope', 'æ': 'appat', 'þ': 'potion', 'ø': 'deguisement', 'œ': 'maillet', 'Ł': 'bandelette', 'Ŧ': 'patate', 'Ŋ': 'meute', 'ĸ': 'fantome', 'Ħ': 'couronne',
  'X': 'caisse-metal', ']': 'rocher',
  'F': 'caisse-bois', 'O': 'caisse-or', 'N': 'caisse-bombe', 'J': 'caisse-bois',
  'y': 'caisse-bois', 'Q': 'caisse-cle', 'd': 'caisse-bois',
}

/* char -> moine. Les cinq se distinguent par la couleur et UN attribut. */
const MOINE_PAR_CHAR: Record<string, string> = {
  'm': 'normal', 'S': 'sauteur', 'G': 'voleur', 'M': 'boss', 'µ': 'batisseur', '`': 'statue',
}

const POISSON_PAR_CHAR: Record<string, string> = { ',': 'piranha', ';': 'meduse' }

/* char -> code de décoration (couche cosmétique). Construit une fois. */
const DECO_CODE_PAR_CHAR = new Map<string, number>()
for (const k of Object.keys(DECOS) as DecoKind[]) DECO_CODE_PAR_CHAR.set(DECOS[k].ch, DECOS[k].code)

/** Les chars de GRAND décor : l'éditeur les dessine lui-même en pleine emprise. */
export const GRAND_DECO_CHARS = new Set<string>(
  (Object.keys(BIG_DECOS) as BigDecoKind[]).map((k) => BIG_DECOS[k].ch),
)

/* ----------------------------------------------------------------- le cache */
/* Clé = char + monde. La TAILLE n'entre pas dans la clé : on ne peint qu'à 48,
   c'est `drawImage` qui réduit. Un zoom de l'éditeur ne coûte donc rien. */
const CACHE = new Map<string, HTMLCanvasElement>()
const CACHE_MAX = 200

function peindre(g: Ctx, ch: string, world: WorldKey): boolean {
  const P = BIOMES[world]
  setDetail(1)
  g.translate(MARGE_X, MARGE)          // voir MARGE / MARGE_X : ce qui déborde de la case

  const tp = TELEPORTEUR_PAR_CHAR[ch]
  if (tp !== undefined) { tuilePapier(g, 0, 0, S, `Teleporter${tp}`, P); return true }

  const tuile = TUILE_PAR_CHAR[ch]
  if (tuile) { tuilePapier(g, 0, 0, S, tuile, P); return true }

  const objet = OBJET_PAR_CHAR[ch]
  if (objet) { objetPapier(g, 0, 0, S, objet); return true }

  const moine = MOINE_PAR_CHAR[ch]
  if (moine) { blitMoine(g, moine, S, 0, 0); return true }

  const poisson = POISSON_PAR_CHAR[ch]
  if (poisson) { fishPapier(g, 0, 0, S, poisson); return true }

  /* le drapeau-crapaud (05/08) : le fanion, figé (t=0) — en jeu il claque au vent. */
  if (ch === 'ð') { drapeauPapier(g, 0, 0, S, 0); return true }

  /* les trois départs : Le Baroudeur lui-même, au repos. Il porte déjà son
     cache interne, mais on le fige ici à 48 pour la même raison que le reste. */
  if (ch === '1' || ch === '2' || ch === '3') {
    /* trois teintes DISTINCTES, choisies ici : l'ancien rendu passait par des
       lignes différentes de la feuille (`runner2`/`runner3`), qui n'ont pas
       d'équivalent en rotation de teinte. Ce qui compte pour le créateur, c'est
       de ne pas confondre les trois départs. */
    drawBaroudeurPapier(g, 'idle', 0, 1, 0, 0, S, { hue: ch === '2' ? 2 : ch === '3' ? 4 : 0 })
    return true
  }

  const code = DECO_CODE_PAR_CHAR.get(ch)
  if (code !== undefined) return blitDeco(g, code, S, 0, 0)

  return false
}

/**
 * Dessine la vignette d'un caractère d'éditeur dans le carré (x, y, size).
 * Renvoie faux si ce char n'a pas de dessin papier : l'appelant retombe alors
 * sur l'ancien sprite, pour qu'aucune case ne puisse se retrouver vide.
 */
export function blitVignette(ctx: Ctx, ch: string, world: WorldKey, x: number, y: number, size: number): boolean {
  const cle = `${ch}|${world}`
  let cv = CACHE.get(cle)
  if (!cv) {
    if (CACHE.has(`!${cle}`)) return false      // déjà su non dessinable : on ne réessaie pas
    cv = document.createElement('canvas')
    cv.width = S + MARGE_X * 2; cv.height = S + MARGE
    const g = cv.getContext('2d')
    if (!g || !peindre(g, ch, world)) { CACHE.set(`!${cle}`, cv); return false }
    if (CACHE.size >= CACHE_MAX) { const k = CACHE.keys().next().value; if (k) CACHE.delete(k) }
    CACHE.set(cle, cv)
  }
  /* le lissage est ce qui rend la réduction propre : sans lui, une vignette de
     30 px reprendrait un pixel sur deux et les franges disparaîtraient. */
  const lissage = ctx.imageSmoothingEnabled
  ctx.imageSmoothingEnabled = true
  const k = size / S
  ctx.drawImage(cv, x - MARGE_X * k, y - MARGE * k, size + MARGE_X * 2 * k, size + MARGE * k)
  ctx.imageSmoothingEnabled = lissage
  return true
}

/**
 * Même chose pour une TUILE de terrain désignée par sa valeur d'enum — ce que
 * parlent les miniatures de niveaux, qui lisent un LevelData et non des chars.
 *
 * Une miniature fait SIX pixels par case. Dessiner du papier à cette taille
 * n'aurait aucun sens ; mais on ne le dessine pas à cette taille : on réduit la
 * tuile peinte à 48, et le lissage en fait la couleur moyenne exacte de la vraie
 * tuile. La miniature est donc juste par construction, sans table de couleurs à
 * tenir à jour en parallèle.
 */
export function blitTuileMini(ctx: Ctx, tile: number, world: WorldKey, x: number, y: number, size: number): boolean {
  const nom = T[tile]
  if (!nom) return false
  const cle = `#${tile}|${world}`
  let cv = CACHE.get(cle)
  if (!cv) {
    cv = document.createElement('canvas')
    cv.width = S; cv.height = S + MARGE
    const g = cv.getContext('2d')
    if (!g) return false
    setDetail(1)
    tuilePapier(g, 0, MARGE, S, nom, BIOMES[world])
    if (CACHE.size >= CACHE_MAX) { const k = CACHE.keys().next().value; if (k) CACHE.delete(k) }
    CACHE.set(cle, cv)
  }
  const lissage = ctx.imageSmoothingEnabled
  ctx.imageSmoothingEnabled = true
  const k = size / S
  ctx.drawImage(cv, x, y - MARGE * k, size, size + MARGE * k)
  ctx.imageSmoothingEnabled = lissage
  return true
}

/** Vide le cache (changement de monde massif, test). */
export function viderVignettes(): void { CACHE.clear() }
